import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import { isPlatformAdminHostname } from '../../config/platformAdminHost'
import {
  CurrencyDollarIcon,
  TableCellsIcon,
  ArrowPathIcon,
  ChartBarIcon,
  Squares2X2Icon,
  ScaleIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function weekAgoISO() {
  const d = new Date()
  d.setDate(d.getDate() - 7)
  return d.toISOString().slice(0, 10)
}

function money(n, currency = 'AUD') {
  return `${currency} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

export default function BillableUsage({ fixedGroupId = '', embedded = false }) {
  const location = useLocation()
  const { user } = useAuth()
  const orgTenantMode = location.pathname === '/admin/usage'
  const scopedTenant = embedded && !!fixedGroupId
  const tenantMode = orgTenantMode || scopedTenant
  const apiBase = orgTenantMode ? '/api/billable-usage' : '/api/platform/billable-usage'

  const [tab, setTab] = useState(scopedTenant ? 'rates' : orgTenantMode ? 'usage' : 'bands')
  const [services, setServices] = useState([])
  const [bands, setBands] = useState([])
  const [summary, setSummary] = useState([])
  const [events, setEvents] = useState([])
  const [settlement, setSettlement] = useState(null)
  const [tenantBilling, setTenantBilling] = useState(null)
  const [loadingServices, setLoadingServices] = useState(() => !tenantMode)
  const [loadingCatalog, setLoadingCatalog] = useState(false)
  const [loadingBands, setLoadingBands] = useState(false)
  const [loadingUsage, setLoadingUsage] = useState(false)
  const [saving, setSaving] = useState({})
  const [from, setFrom] = useState(weekAgoISO)
  const [to, setTo] = useState(todayISO)
  const [filterGroupId, setFilterGroupId] = useState(fixedGroupId || '')
  const [tenants, setTenants] = useState([])
  const [tenantsLoading, setTenantsLoading] = useState(false)
  const [rateDrafts, setRateDrafts] = useState({})
  const [bandMinDrafts, setBandMinDrafts] = useState({})
  const [bandRateDrafts, setBandRateDrafts] = useState({})
  const [tenantBandDraft, setTenantBandDraft] = useState('B')
  const [tenantMinOverride, setTenantMinOverride] = useState('')
  const [tenantYearStart, setTenantYearStart] = useState('')
  const [planHistory, setPlanHistory] = useState([])

  useEffect(() => {
    if (fixedGroupId) setFilterGroupId(fixedGroupId)
  }, [fixedGroupId])

  const effectiveGroupId = scopedTenant ? fixedGroupId : filterGroupId

  const loadTenants = useCallback(async () => {
    setTenantsLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants'))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      const list = Array.isArray(data.tenants) ? data.tenants : []
      list.sort((a, b) =>
        String(a.group_id || '').localeCompare(String(b.group_id || ''), undefined, { sensitivity: 'base' })
      )
      setTenants(list)
    } catch (e) {
      toast.error(e.message || 'Failed to load tenants')
      setTenants([])
    } finally {
      setTenantsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (tenantMode || scopedTenant) return
    loadTenants()
  }, [loadTenants, tenantMode, scopedTenant])

  const loadServices = useCallback(async () => {
    const res = await authFetch(getLocalApiUrl(`${apiBase}/services`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to load services')
    setServices(json.data || [])
    const drafts = {}
    for (const s of json.data || []) {
      drafts[s.service_code] = {
        unit_rate_amount: s.unit_rate_amount,
        currency: s.currency || 'AUD',
      }
    }
    setRateDrafts(drafts)
  }, [apiBase])

  const loadBands = useCallback(async () => {
    if (orgTenantMode) return
    setLoadingBands(true)
    try {
      const res = await authFetch(getLocalApiUrl(`${apiBase}/bands`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load plans')
      const list = json.data || []
      setBands(list)
      const mins = {}
      const rates = {}
      for (const b of list) {
        mins[b.band_code] = b.min_annual_spend
        rates[b.band_code] = {}
        for (const r of b.rates || []) {
          rates[b.band_code][r.service_code] = r.unit_rate_amount
        }
      }
      setBandMinDrafts(mins)
      setBandRateDrafts(rates)
    } finally {
      setLoadingBands(false)
    }
  }, [apiBase, orgTenantMode])

  const loadSummary = useCallback(async () => {
    const qs = new URLSearchParams()
    if (from) qs.set('from', from)
    if (to) qs.set('to', to)
    if (!orgTenantMode && effectiveGroupId.trim()) qs.set('group_id', effectiveGroupId.trim())
    const res = await authFetch(getLocalApiUrl(`${apiBase}/usage/summary?${qs}`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to load summary')
    setSummary(json.data || [])
  }, [from, to, effectiveGroupId, orgTenantMode, apiBase])

  const loadEvents = useCallback(async () => {
    const qs = new URLSearchParams()
    if (from) qs.set('from', from)
    if (to) qs.set('to', to)
    if (!orgTenantMode && effectiveGroupId.trim()) qs.set('group_id', effectiveGroupId.trim())
    qs.set('limit', '200')
    const res = await authFetch(getLocalApiUrl(`${apiBase}/usage/events?${qs}`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to load events')
    setEvents(json.data || [])
  }, [from, to, effectiveGroupId, orgTenantMode, apiBase])

  const loadTenantBilling = useCallback(async (gid) => {
    if (!gid || orgTenantMode) {
      setTenantBilling(null)
      return
    }
    try {
      const res = await authFetch(
        getLocalApiUrl(`${apiBase}/tenants/${encodeURIComponent(gid)}/billing`)
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load tenant billing')
      setTenantBilling(json.data)
      setTenantBandDraft(json.data.billing_band || 'B')
      setTenantMinOverride(
        json.data.min_annual_spend_override != null ? String(json.data.min_annual_spend_override) : ''
      )
      setTenantYearStart(json.data.billing_year_start || '')
    } catch (e) {
      setTenantBilling(null)
      toast.error(e.message || 'Tenant billing load failed')
    }
  }, [apiBase, orgTenantMode])

  const loadPlanHistory = useCallback(async (gid) => {
    if (!gid || orgTenantMode) {
      setPlanHistory([])
      return
    }
    try {
      const res = await authFetch(
        getLocalApiUrl(`${apiBase}/tenants/${encodeURIComponent(gid)}/billing/history`)
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load plan history')
      setPlanHistory(json.data || [])
    } catch (_) {
      setPlanHistory([])
    }
  }, [apiBase, orgTenantMode])

  const loadSettlement = useCallback(async (gid) => {
    if (!gid) {
      setSettlement(null)
      return
    }
    try {
      const qs = orgTenantMode ? '' : `?group_id=${encodeURIComponent(gid)}`
      const res = await authFetch(getLocalApiUrl(`${apiBase}/usage/settlement${qs}`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Settlement failed')
      setSettlement(json.data)
    } catch (e) {
      setSettlement(null)
      toast.error(e.message || 'Settlement failed')
    }
  }, [apiBase, orgTenantMode])

  useEffect(() => {
    if (orgTenantMode) return
    let cancelled = false
    ;(async () => {
      setLoadingServices(true)
      try {
        await Promise.all([loadServices(), loadBands()])
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Load failed')
      } finally {
        if (!cancelled) setLoadingServices(false)
      }
    })()
    return () => { cancelled = true }
  }, [loadServices, loadBands, orgTenantMode])

  useEffect(() => {
    if (orgTenantMode || !effectiveGroupId.trim()) return
    loadTenantBilling(effectiveGroupId.trim())
    loadPlanHistory(effectiveGroupId.trim())
  }, [orgTenantMode, effectiveGroupId, loadTenantBilling, loadPlanHistory])

  useEffect(() => {
    if (!tenantMode || tab !== 'rates') return
    if (services.length > 0) return
    let cancelled = false
    ;(async () => {
      setLoadingCatalog(true)
      try {
        await loadServices()
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Load failed')
      } finally {
        if (!cancelled) setLoadingCatalog(false)
      }
    })()
    return () => { cancelled = true }
  }, [loadServices, tenantMode, tab, services.length])

  useEffect(() => {
    if (tab !== 'usage' && tab !== 'settlement') return
    let cancelled = false
    ;(async () => {
      setLoadingUsage(true)
      try {
        if (tab === 'usage') {
          await loadSummary()
          if (!cancelled) await loadEvents()
        }
        const gid = orgTenantMode ? user?.group_id : effectiveGroupId.trim()
        if (gid) {
          if (!orgTenantMode) await loadTenantBilling(gid)
          if (tab === 'settlement' || (tab === 'usage' && gid)) await loadSettlement(gid)
        } else {
          setSettlement(null)
          setTenantBilling(null)
        }
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Load failed')
      } finally {
        if (!cancelled) setLoadingUsage(false)
      }
    })()
    return () => { cancelled = true }
  }, [
    tab, from, to, effectiveGroupId, loadSummary, loadEvents,
    loadTenantBilling, loadSettlement, orgTenantMode, user?.group_id,
  ])

  const refreshUsage = async () => {
    setLoadingUsage(true)
    try {
      await loadSummary()
      await loadEvents()
      const gid = orgTenantMode ? user?.group_id : effectiveGroupId.trim()
      if (gid) {
        if (!orgTenantMode) await loadTenantBilling(gid)
        await loadSettlement(gid)
      }
      toast.success('Usage refreshed')
    } catch (e) {
      toast.error(e.message || 'Refresh failed')
    } finally {
      setLoadingUsage(false)
    }
  }

  const saveRate = async (serviceCode) => {
    if (tenantMode) return
    const draft = rateDrafts[serviceCode]
    if (!draft) return
    setSaving((s) => ({ ...s, [serviceCode]: true }))
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/platform/billable-usage/services/${encodeURIComponent(serviceCode)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unit_rate_amount: Number(draft.unit_rate_amount),
            currency: draft.currency || 'AUD',
          }),
        }
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      toast.success(`Updated catalog ${serviceCode}`)
      await loadServices()
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving((s) => ({ ...s, [serviceCode]: false }))
    }
  }

  const saveBandMin = async (bandCode) => {
    setSaving((s) => ({ ...s, [`band-min-${bandCode}`]: true }))
    try {
      const res = await authFetch(
        getLocalApiUrl(`${apiBase}/bands/${encodeURIComponent(bandCode)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ min_annual_spend: Number(bandMinDrafts[bandCode]) }),
        }
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      toast.success(`Plan ${bandCode} minimum saved`)
      await loadBands()
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving((s) => ({ ...s, [`band-min-${bandCode}`]: false }))
    }
  }

  const saveBandRate = async (bandCode, serviceCode) => {
    const key = `band-rate-${bandCode}-${serviceCode}`
    setSaving((s) => ({ ...s, [key]: true }))
    try {
      const amount = bandRateDrafts[bandCode]?.[serviceCode]
      const res = await authFetch(
        getLocalApiUrl(
          `${apiBase}/bands/${encodeURIComponent(bandCode)}/rates/${encodeURIComponent(serviceCode)}`
        ),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unit_rate_amount: Number(amount) }),
        }
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      toast.success(`${bandCode}/${serviceCode} rate saved`)
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving((s) => ({ ...s, [key]: false }))
    }
  }

  const saveTenantBilling = async () => {
    const gid = effectiveGroupId.trim()
    if (!gid) return
    setSaving((s) => ({ ...s, tenantBilling: true }))
    try {
      const body = {
        billing_band: tenantBandDraft,
        min_annual_spend_override: tenantMinOverride === '' ? null : Number(tenantMinOverride),
        billing_year_start: tenantYearStart || null,
      }
      const res = await authFetch(
        getLocalApiUrl(`${apiBase}/tenants/${encodeURIComponent(gid)}/billing`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      toast.success('Tenant plan saved')
      await loadTenantBilling(gid)
      await loadPlanHistory(gid)
      await loadSettlement(gid)
      await loadSummary()
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving((s) => ({ ...s, tenantBilling: false }))
    }
  }

  if (orgTenantMode && isPlatformAdminHostname()) {
    return (
      <div className="bg-white shadow rounded-lg p-6 max-w-lg space-y-2">
        <h1 className="text-xl font-semibold text-gray-900">Third-party usage</h1>
        <p className="text-gray-600">
          Organisation usage is on a tenant URL. For plans and all tenants, use <strong>Billable usage</strong>.
        </p>
      </div>
    )
  }

  if (orgTenantMode && !user?.group_id) {
    return (
      <div className="bg-white shadow rounded-lg p-6 max-w-lg">
        <h1 className="text-xl font-semibold text-gray-900">Third-party usage</h1>
        <p className="mt-2 text-gray-600">Your account is not linked to an organisation.</p>
      </div>
    )
  }

  if (!tenantMode && loadingServices) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  const matrixCodes =
    bands[0]?.rates?.map((r) => r.service_code) ||
    services.map((s) => s.service_code)

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <CurrencyDollarIcon className="h-8 w-8 text-primary-600" />
            {tenantMode ? 'Third-party usage' : 'Billable third-party usage'}
          </h1>
          <p className="text-gray-600">
            {tenantMode ? (
              <>Usage priced by your organisation&rsquo;s band. Annual minimum applies only at year-end settlement.</>
            ) : (
              <>
                Plans A–D set unit rates and platform annual minimums. Assign a tenant to a plan;
                optional negotiated minimum overrides the plan floor at year-end (Shortfall).
              </>
            )}
          </p>
        </div>
      )}

      <div className="border-b border-gray-200 bg-white rounded-t-lg px-4 flex gap-4 flex-wrap">
        {!tenantMode && (
          <button
            type="button"
            onClick={() => setTab('bands')}
            className={`py-3 px-2 border-b-2 text-sm font-medium ${
              tab === 'bands' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500'
            }`}
          >
            <span className="inline-flex items-center gap-1">
              <Squares2X2Icon className="h-4 w-4" /> Plans A–D
            </span>
          </button>
        )}
        <button
          type="button"
          onClick={() => setTab('rates')}
          className={`py-3 px-2 border-b-2 text-sm font-medium ${
            tab === 'rates' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500'
          }`}
        >
          <span className="inline-flex items-center gap-1">
            <TableCellsIcon className="h-4 w-4" /> {tenantMode ? 'Your rates' : 'Catalog fallback'}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setTab('usage')}
          className={`py-3 px-2 border-b-2 text-sm font-medium ${
            tab === 'usage' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500'
          }`}
        >
          <span className="inline-flex items-center gap-1">
            <ChartBarIcon className="h-4 w-4" /> Usage &amp; audit
          </span>
        </button>
        <button
          type="button"
          onClick={() => setTab('settlement')}
          className={`py-3 px-2 border-b-2 text-sm font-medium ${
            tab === 'settlement' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500'
          }`}
        >
          <span className="inline-flex items-center gap-1">
            <ScaleIcon className="h-4 w-4" /> Year-end settlement
          </span>
        </button>
      </div>

      {tab === 'bands' && !tenantMode && (
        <div className="space-y-4">
          {loadingBands && <p className="text-sm text-gray-500">Loading bands…</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {bands.map((b) => (
              <div key={b.band_code} className="bg-white shadow rounded-lg p-4 border border-gray-100">
                <div className="font-semibold text-gray-900">{b.name}</div>
                <p className="text-xs text-gray-500 mt-1 min-h-[2.5rem]">{b.description || ''}</p>
                <label className="block text-xs font-medium text-gray-500 mt-3 mb-1">
                  Platform annual minimum
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    step="0.01"
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                    value={bandMinDrafts[b.band_code] ?? ''}
                    onChange={(e) =>
                      setBandMinDrafts((d) => ({ ...d, [b.band_code]: e.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="btn-secondary text-xs shrink-0"
                    disabled={!!saving[`band-min-${b.band_code}`]}
                    onClick={() => saveBandMin(b.band_code)}
                  >
                    Save
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Unit rates by band</h2>
              <p className="text-sm text-gray-500 mt-1">
                These rates price usage during the year. Save each cell after editing.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Service</th>
                    {bands.map((b) => (
                      <th key={b.band_code} className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                        Plan {b.band_code}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {matrixCodes.map((code) => {
                    const label =
                      bands[0]?.rates?.find((r) => r.service_code === code)?.service_name || code
                    return (
                      <tr key={code}>
                        <td className="px-3 py-2">
                          <span className="font-mono font-medium">{code}</span>
                          <span className="text-gray-500 ml-2 text-xs">{label}</span>
                        </td>
                        {bands.map((b) => {
                          const key = `band-rate-${b.band_code}-${code}`
                          return (
                            <td key={b.band_code} className="px-3 py-2 text-right">
                              <div className="inline-flex items-center gap-1">
                                <input
                                  type="number"
                                  step="0.0001"
                                  className="w-24 rounded border border-gray-300 px-1.5 py-1 text-right text-xs"
                                  value={bandRateDrafts[b.band_code]?.[code] ?? ''}
                                  onChange={(e) =>
                                    setBandRateDrafts((d) => ({
                                      ...d,
                                      [b.band_code]: {
                                        ...(d[b.band_code] || {}),
                                        [code]: e.target.value,
                                      },
                                    }))
                                  }
                                />
                                <button
                                  type="button"
                                  className="text-[10px] text-primary-700 hover:underline"
                                  disabled={!!saving[key]}
                                  onClick={() => saveBandRate(b.band_code, code)}
                                >
                                  Save
                                </button>
                              </div>
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!orgTenantMode && effectiveGroupId.trim() && tab === 'rates' && (
        <div className="bg-white shadow rounded-lg p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">
            Tenant plan — <span className="font-mono">{effectiveGroupId}</span>
          </h2>
          <p className="text-xs text-gray-500">
            Plan A = highest rates/minimum; Plan D = lowest. Negotiated annual minimum overrides the plan default (year-end Shortfall floor).
          </p>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Plan</label>
              <select
                className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                value={tenantBandDraft}
                onChange={(e) => setTenantBandDraft(e.target.value)}
              >
                {['A', 'B', 'C', 'D'].map((c) => (
                  <option key={c} value={c}>Plan {c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Negotiated annual minimum (blank = plan default)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder={
                  tenantBilling?.band_min_annual_spend != null
                    ? `Plan default ${tenantBilling.band_min_annual_spend}`
                    : 'Plan default'
                }
                className="rounded border border-gray-300 px-2 py-1 text-sm w-48"
                value={tenantMinOverride}
                onChange={(e) => setTenantMinOverride(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Contract anniversary (optional)
              </label>
              <input
                type="date"
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                value={tenantYearStart}
                onChange={(e) => setTenantYearStart(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={!!saving.tenantBilling}
              onClick={saveTenantBilling}
            >
              {saving.tenantBilling ? 'Saving…' : 'Save tenant plan'}
            </button>
          </div>
          {tenantBilling && (
            <p className="text-xs text-gray-600">
              Resolved minimum: <strong>{money(tenantBilling.resolved_min_annual_spend, tenantBilling.currency)}</strong>
              {' '}({tenantBilling.minimum_source === 'tenant_override' ? 'negotiated override' : 'plan default'})
            </p>
          )}
          {planHistory.length > 0 && (
            <div className="mt-2 border-t border-gray-100 pt-2">
              <div className="text-xs font-medium text-gray-500 mb-1">Plan history</div>
              <ul className="text-xs text-gray-600 space-y-1 max-h-36 overflow-y-auto">
                {planHistory.map((h) => (
                  <li key={h.id}>
                    <span className="font-mono font-medium">Plan {h.plan_code}</span>
                    {' from '}
                    {h.effective_from ? new Date(h.effective_from).toLocaleString() : '—'}
                    {h.effective_to
                      ? ` → ${new Date(h.effective_to).toLocaleString()}`
                      : ' → current'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'rates' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {tenantMode ? 'Rates for your plan' : 'Catalog fallback rates'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {tenantMode
                ? 'Shown at your assigned plan rates (Plan A highest → Plan D lowest).'
                : 'Used only when a band rate is missing. Prefer editing Plans A–D for live pricing.'}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  {!tenantMode && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingCatalog && services.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500 text-sm">
                      Loading…
                    </td>
                  </tr>
                ) : (
                  services.map((s) => (
                    <tr key={s.service_code}>
                      <td className="px-4 py-3 text-sm font-mono font-medium">{s.service_code}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{s.name}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        {tenantMode ? (
                          <span className="tabular-nums">
                            {bandRateDrafts[tenantBandDraft || tenantBilling?.billing_band || 'B']?.[s.service_code]
                              ?? s.unit_rate_amount
                              ?? '—'}
                          </span>
                        ) : (
                          <input
                            type="number"
                            step="0.0001"
                            className="w-28 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                            value={rateDrafts[s.service_code]?.unit_rate_amount ?? ''}
                            onChange={(e) =>
                              setRateDrafts((d) => ({
                                ...d,
                                [s.service_code]: {
                                  ...d[s.service_code],
                                  unit_rate_amount: e.target.value,
                                  currency: d[s.service_code]?.currency || s.currency,
                                },
                              }))
                            }
                          />
                        )}
                      </td>
                      {!tenantMode && (
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="btn-secondary text-sm"
                            disabled={!!saving[s.service_code]}
                            onClick={() => saveRate(s.service_code)}
                          >
                            {saving[s.service_code] ? 'Saving…' : 'Save'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(tab === 'usage' || tab === 'settlement') && (
        <div className="space-y-4">
          {!tenantMode && !scopedTenant && (
            <div className="bg-white shadow rounded-lg p-4 flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Tenant</label>
                <select
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm min-w-[14rem]"
                  value={filterGroupId}
                  onChange={(e) => setFilterGroupId(e.target.value)}
                  disabled={tenantsLoading}
                >
                  <option value="">All tenants</option>
                  {tenants.map((t) => {
                    const gid = t.group_id
                    if (!gid) return null
                    const band = t.billing_band ? ` · Plan ${t.billing_band}` : ''
                    return (
                      <option key={gid} value={gid}>
                        {(t.name || gid) + ` (${gid})${band}`}
                      </option>
                    )
                  })}
                </select>
              </div>
              {tab === 'usage' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                    <input type="date" className="rounded border border-gray-300 px-2 py-1 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                    <input type="date" className="rounded border border-gray-300 px-2 py-1 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
                  </div>
                </>
              )}
              <button type="button" className="btn-primary inline-flex items-center gap-1" onClick={refreshUsage} disabled={loadingUsage}>
                <ArrowPathIcon className={`h-4 w-4 ${loadingUsage ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          )}

          {!orgTenantMode && effectiveGroupId.trim() && (
            <div className="bg-white shadow rounded-lg p-4 space-y-3">
              <h2 className="text-sm font-semibold text-gray-900">
                Tenant plan — <span className="font-mono">{effectiveGroupId}</span>
              </h2>
              <p className="text-xs text-gray-500">
                Plan A = highest rates/minimum; Plan D = lowest. Negotiated annual minimum overrides the plan default (year-end Shortfall floor).
              </p>
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Plan</label>
                  <select
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                    value={tenantBandDraft}
                    onChange={(e) => setTenantBandDraft(e.target.value)}
                  >
                    {['A', 'B', 'C', 'D'].map((c) => (
                      <option key={c} value={c}>Plan {c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Negotiated annual minimum (blank = plan default)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={
                      tenantBilling?.band_min_annual_spend != null
                        ? `Plan default ${tenantBilling.band_min_annual_spend}`
                        : 'Plan default'
                    }
                    className="rounded border border-gray-300 px-2 py-1 text-sm w-48"
                    value={tenantMinOverride}
                    onChange={(e) => setTenantMinOverride(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Contract anniversary (optional)
                  </label>
                  <input
                    type="date"
                    className="rounded border border-gray-300 px-2 py-1 text-sm"
                    value={tenantYearStart}
                    onChange={(e) => setTenantYearStart(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn-primary text-sm"
                  disabled={!!saving.tenantBilling}
                  onClick={saveTenantBilling}
                >
                  {saving.tenantBilling ? 'Saving…' : 'Save tenant plan'}
                </button>
              </div>
              {tenantBilling && (
                <p className="text-xs text-gray-600">
                  Resolved minimum: <strong>{money(tenantBilling.resolved_min_annual_spend, tenantBilling.currency)}</strong>
                  {' '}({tenantBilling.minimum_source === 'tenant_override' ? 'negotiated override' : 'plan default'})
                </p>
              )}
              {planHistory.length > 0 && (
                <div className="mt-2 border-t border-gray-100 pt-2">
                  <div className="text-xs font-medium text-gray-500 mb-1">Plan history</div>
                  <ul className="text-xs text-gray-600 space-y-1 max-h-36 overflow-y-auto">
                    {planHistory.map((h) => (
                      <li key={h.id}>
                        <span className="font-mono font-medium">Plan {h.plan_code}</span>
                        {' from '}
                        {h.effective_from ? new Date(h.effective_from).toLocaleString() : '—'}
                        {h.effective_to
                          ? ` → ${new Date(h.effective_to).toLocaleString()}`
                          : ' → current'}
                        {h.min_annual_spend_override != null
                          ? ` · negotiated min ${h.min_annual_spend_override}`
                          : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {tab === 'settlement' && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Year-end settlement</h2>
              {!effectiveGroupId.trim() && !orgTenantMode ? (
                <p className="text-sm text-gray-500">Select a tenant to preview settlement.</p>
              ) : !settlement ? (
                <p className="text-sm text-gray-500">Loading settlement…</p>
              ) : (
                <div className="space-y-3 text-sm">
                  <p className="text-gray-600">
                    Period {settlement.period_start} → {settlement.period_end_exclusive} (exclusive end) · Plan{' '}
                    <strong>{settlement.billing_band}</strong>
                  </p>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                    <div className="rounded border border-gray-200 px-3 py-2">
                      <dt className="text-xs text-gray-500">Usage total (plan rates)</dt>
                      <dd className="text-lg font-semibold">{money(settlement.usage_total, settlement.currency)}</dd>
                    </div>
                    <div className="rounded border border-gray-200 px-3 py-2">
                      <dt className="text-xs text-gray-500">
                        Resolved minimum ({settlement.minimum_source === 'tenant_override' ? 'negotiated' : 'plan'})
                      </dt>
                      <dd className="text-lg font-semibold">{money(settlement.minimum, settlement.currency)}</dd>
                    </div>
                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
                      <dt className="text-xs text-amber-800">Shortfall</dt>
                      <dd className="text-lg font-semibold text-amber-900">
                        {money((settlement.shortfall ?? settlement.top_up), settlement.currency)}
                      </dd>
                    </div>
                    <div className="rounded border border-teal-200 bg-teal-50 px-3 py-2">
                      <dt className="text-xs text-teal-800">Amount due</dt>
                      <dd className="text-lg font-semibold text-teal-900">
                        {money(settlement.amount_due, settlement.currency)}
                      </dd>
                    </div>
                  </dl>
                  <p className="text-xs text-gray-500">
                    If usage is below the minimum, the difference is added at year-end so the invoice reaches the minimum.
                  </p>
                </div>
              )}
            </div>
          )}

          {tab === 'usage' && (
            <>
              {settlement && effectiveGroupId.trim() && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  Contract year to date: usage <strong>{money(settlement.usage_total, settlement.currency)}</strong>
                  {' · '}minimum <strong>{money(settlement.minimum, settlement.currency)}</strong>
                  {(settlement.shortfall ?? settlement.top_up) > 0 ? (
                    <> · projected Shortfall <strong>{money((settlement.shortfall ?? settlement.top_up), settlement.currency)}</strong></>
                  ) : (
                    <> · minimum already met</>
                  )}
                </div>
              )}

              {(tenantMode || scopedTenant) && (
                <div className="bg-white shadow rounded-lg p-4 flex flex-wrap gap-4 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                    <input type="date" className="rounded border border-gray-300 px-2 py-1 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                    <input type="date" className="rounded border border-gray-300 px-2 py-1 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
                  </div>
                  <button type="button" className="btn-primary inline-flex items-center gap-1" onClick={refreshUsage} disabled={loadingUsage}>
                    <ArrowPathIcon className={`h-4 w-4 ${loadingUsage ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              )}

              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {tenantMode ? 'Summary by service' : 'Summary by tenant & service'}
                    <span className="text-sm font-normal text-gray-500 ml-2">(plan rates; stubs excluded)</span>
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {!tenantMode && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">group_id</th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Events</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Units</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Est. amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {summary.length === 0 ? (
                        <tr>
                          <td colSpan={tenantMode ? 5 : 7} className="px-4 py-8 text-center text-gray-500 text-sm">
                            No usage in this range.
                          </td>
                        </tr>
                      ) : (
                        summary.map((row, i) => (
                          <tr key={`${row.group_id}-${row.service_code}-${i}`}>
                            {!tenantMode && (
                              <td className="px-4 py-3 text-sm font-mono text-gray-800">{row.group_id}</td>
                            )}
                            <td className="px-4 py-3 text-sm font-mono">{row.billing_plan || row.billing_band || '—'}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="font-mono font-medium">{row.service_code}</span>
                              <span className="text-gray-500 ml-2">{row.service_name}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right">{row.event_count}</td>
                            <td className="px-4 py-3 text-sm text-right">{row.total_units}</td>
                            <td className="px-4 py-3 text-sm text-right tabular-nums">
                              {Number(row.unit_rate_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {money(row.estimated_amount, row.currency || 'AUD')}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Recent events</h2>
                </div>
                <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                        {!tenantMode && (
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">group_id</th>
                        )}
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Svc</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Plan</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Rate</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Outcome</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Integration</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Stub</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {events.length === 0 ? (
                        <tr>
                          <td colSpan={tenantMode ? 7 : 8} className="px-4 py-6 text-center text-gray-500">
                            No events.
                          </td>
                        </tr>
                      ) : (
                        events.map((ev) => (
                          <tr key={ev.id}>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                              {ev.occurred_at ? new Date(ev.occurred_at).toLocaleString() : '—'}
                            </td>
                            {!tenantMode && (
                              <td className="px-3 py-2 font-mono text-xs">{ev.group_id}</td>
                            )}
                            <td className="px-3 py-2 font-mono">{ev.service_code}</td>
                            <td className="px-3 py-2 font-mono">{ev.billing_plan || (ev.metadata && ev.metadata.billing_plan) || '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {ev.billed_unit_rate != null
                                ? Number(ev.billed_unit_rate).toLocaleString(undefined, { maximumFractionDigits: 4 })
                                : (ev.metadata && ev.metadata.billed_unit_rate != null
                                  ? Number(ev.metadata.billed_unit_rate).toLocaleString(undefined, { maximumFractionDigits: 4 })
                                  : '—')}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{ev.outcome || '—'}</td>
                            <td className="px-3 py-2 text-gray-600">{ev.integration_point || '—'}</td>
                            <td className="px-3 py-2 text-center">{ev.is_stub ? 'yes' : 'no'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
